import { ElementRef, Injectable } from '@angular/core';
import { InfoItem, ResponsiveService } from '@frk/eds-components';
import { Subject, BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, mapTo, takeUntil } from 'rxjs/operators';

@Injectable()
export class TableResponsiveService {
  private cardsSubject$: Subject<InfoItem[][]> = new Subject();

  private onDestroy$ = new Subject<boolean>();

  private thead: HTMLTableSectionElement;
  private tbody: HTMLTableSectionElement;

  private theadChanged$ = new BehaviorSubject(true);
  private tbodyChanged$ = new Subject<boolean>();

  private theadObserver = new MutationObserver(() =>
    this.theadChanged$.next(true)
  );
  private tbodyObserver = new MutationObserver(() =>
    this.tbodyChanged$.next(true)
  );

  public get cards$(): Observable<InfoItem[][]> {
    return this.cardsSubject$.asObservable();
  }

  private isTable = false;

  public init(table: ElementRef) {
    if (table && !this.isTable) {
      this.isTable = true;
      this.thead = table.nativeElement.querySelector('thead');
      this.tbody = table.nativeElement.querySelector('tbody');
      const [columns, cells] = this.mapTable(
        this.thead.rows.item(0),
        this.tbody.rows
      );
      this.createCards(columns, cells);
      this.theadObserver.observe(this.thead, {
        characterData: true,
        characterDataOldValue: true,
        subtree: true,
      });
      this.tbodyObserver.observe(this.tbody, { childList: true });

      combineLatest([this.theadChanged$, this.tbodyChanged$])
        .pipe(
          mapTo([this.thead.rows.item(0), this.tbody.rows]),
          map(
            ([headRow, bodyRows]: [
              HTMLTableRowElement,
              HTMLCollectionOf<HTMLTableRowElement>
            ]) => {
              return this.mapTable(headRow, bodyRows);
            }
          ),
          takeUntil(this.onDestroy$)
        )
        .subscribe(
          ([columnNames, rows]: [Element[], HTMLTableCellElement[][]]) => {
            this.createCards(columnNames, rows);
          }
        );
    }
  }

  mapTable(
    headRow: HTMLTableRowElement,
    bodyRows: HTMLCollectionOf<HTMLTableRowElement>
  ): [Element[], HTMLTableCellElement[][]] {
    const returnValue: [Element[], HTMLTableCellElement[][]] = [[], []];
    for (let index = 0; index < headRow.children.length; index++) {
      returnValue[0].push(headRow.children.item(index));
    }
    for (let index = 0; index < bodyRows.length; index++) {
      const rows = [];
      for (let row = 0; row < bodyRows.item(index).children.length; row++) {
        rows.push(bodyRows.item(index).children.item(row));
      }
      returnValue[1].push(rows);
    }
    return returnValue;
  }

  private createCards(columnNames: Element[], rows: HTMLTableCellElement[][]) {
    const cards: InfoItem[][] = [];
    rows.forEach((rowCells) => {
      const infoItems: InfoItem[] = [];
      rowCells.forEach((cell) => {
        let label = columnNames[cell.cellIndex].textContent;
        let val = cell.textContent;
        let labelSmallText = '';
        let valueSmallText = '';
        if (columnNames[cell.cellIndex].children.length > 1) {
          label = columnNames[cell.cellIndex].children.item(0).textContent;
          labelSmallText = columnNames[cell.cellIndex].children.item(1)
            .textContent;
        }
        if (cell.children.length > 1) {
          val = cell.children.item(0).textContent;
          valueSmallText = cell.children.item(1).textContent;
        }
        if (label.trim() !== '') {
          infoItems.push({
            label,
            labelSmallText,
            val,
            valueSmallText,
          });
        }
      });
      cards.push(infoItems);
    });
    this.cardsSubject$.next(cards);
  }

  removeAndGetValue(item: InfoItem): string {
    const value = item.val;
    item.val = '';
    return value;
  }

  removeValue(item: InfoItem): boolean {
    const value = item.val;
    item.valueSmallText = '';
    item.val = '';
    return true;
  }

  onDestroy(): void {
    this.theadObserver.disconnect();
    this.tbodyObserver.disconnect();
    this.onDestroy$.next(true);
  }
}
